import type { BasicUser } from '@st/sdk'
import { GroupedSelect, SearchIcon, Select, ToggleSwitch } from '@st/theme'
import {
  STFolderListFilters,
  UNASSIGNED,
  statusFilterForId,
  statusFilterId,
  STATUS_FILTER_SECTIONS
} from './st-folder-list-filters-module'
import type { STFolderListMessage } from './st-folder-list-module'

export function STFolderListFiltersBar({
  filters,
  send,
  organizationUsers
}: {
  filters: STFolderListFilters
  send: (message: STFolderListMessage) => void
  organizationUsers: BasicUser[]
}) {
  const assigneeOptions = [
    { label: 'Unassigned', value: UNASSIGNED },
    ...organizationUsers.map((user) => ({
      label: user.name || user.email,
      value: user.id
    }))
  ]
  return (
    <div className="flex h-16 flex-row items-center gap-4">
      <div className="relative w-60 rounded-md shadow-sm">
        <input
          type="text"
          placeholder="Client, email, or tags"
          className="block w-full rounded-md border-0 py-1.5 pl-2 pr-10 text-base text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600"
          // prevent 1password and lastpass from autofilling
          data-lpignore="true"
          data-1p-ignore="true"
          autoFocus={false}
          value={filters.keywords}
          onChange={(e) =>
            send({ type: 'updateFilters', filters: { ...filters, keywords: e.target.value } })
          }
        />
        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
          <SearchIcon className="h-5 w-5 text-gray-400" />
        </div>
      </div>

      <div className="flex flex-row items-center justify-start gap-2">
        <Select
          options={[2025, 2024, 2023, 2022, 2021, 2020]}
          placeholder="Tax year"
          value={filters.year}
          onChange={(year) => send({ type: 'updateFilters', filters: { ...filters, year } })}
          buildLabel={(k) => k}
          buildValue={(v) => v}
        />
      </div>

      <div className="flex flex-row items-center justify-start gap-2">
        <Select
          options={['1040', '1120', '1120s', '1065']}
          placeholder="Return type"
          value={filters.type}
          onChange={(type) => send({ type: 'updateFilters', filters: { ...filters, type } })}
          buildLabel={(k) => k}
          buildValue={(v) => v}
        />
      </div>

      <div className="flex flex-row items-center justify-start gap-2">
        <label>
          <span className="sr-only">Status</span>
          <GroupedSelect
            className="py-2 text-base sm:text-base"
            placeholder="Status"
            sections={STATUS_FILTER_SECTIONS}
            value={filters.status && statusFilterId(filters.status)}
            onChange={(status) =>
              send({
                type: 'updateFilters',
                filters: { ...filters, status: statusFilterForId(status) }
              })
            }
            buildHeader={(section) => section.heading}
            buildValue={(option) => statusFilterId(option.filter)}
            buildLabel={(option) => option.label}
          />
        </label>
      </div>

      <div className="flex flex-row items-center justify-start gap-2">
        <Select
          options={assigneeOptions}
          placeholder="Assignee"
          value={filters.assigneeId}
          onChange={(assigneeId) =>
            send({
              type: 'updateFilters',
              filters: { ...filters, assigneeId }
            })
          }
          buildLabel={(option) => option.label}
          buildValue={(option) => option.value}
        />
      </div>

      <div className="flex flex-row items-center gap-2">
        <label className="text-base">Include archived</label>
        <ToggleSwitch
          aria-label="Include archived"
          value={filters.showArchivedFolders}
          onChange={(value) =>
            send({
              type: 'updateFilters',
              filters: { ...filters, showArchivedFolders: value }
            })
          }
        />
      </div>
    </div>
  )
}
